/*
 * Our own videos in the buy column (LICHT-BESLUITEN L40). The list is chosen in one place,
 * the section "Video's in de koopkolom" (sections/jc-product-videos.liquid, rendered by the
 * layout in a section group), which writes the row for this product into an inert
 * <template>; this element, the slot at the end of the buy column
 * (snippets/jc-product-video.liquid), puts it in place, and again when the theme editor
 * reloads that section. Always without sound (Thomas 09-10: "ik wil de video's zonder geluid"): every video is
 * muted and stays muted.
 *
 * Light: a card video has no src and preload none until the card is at least 60% on
 * screen. Of the cards on screen ONE plays; when it ends the next card on screen plays,
 * after the last the first again. Off screen a card pauses. HLS where the browser plays
 * it itself (Safari, iOS), else the mp4. With prefers-reduced-motion or Save-Data
 * nothing plays on its own; the posters stay.
 *
 * A click on a card opens a native <dialog> (the page behind is inert, Escape closes
 * it) with that video large, looping, and a pause button; closing it drops that source
 * and gives focus back to the card.
 */
if (!customElements.get('jc-product-video')) {
  const VISIBLE = 0.6;
  const HLS = 'application/vnd.apple.mpegurl';

  /** HLS when the browser plays it natively, else the smallest mp4 at least as tall as shown. */
  const pickSource = (card, video, shownHeight) => {
    if (card.dataset.hls && video.canPlayType(HLS)) return card.dataset.hls;
    const sources = (card.dataset.sources || '')
      .split('|')
      .map((part) => part.trim().split(' '))
      .filter(([height, url]) => height && url)
      .map(([height, url]) => ({ height: Number(height), url }))
      .sort((a, b) => a.height - b.height);
    if (!sources.length) return '';
    const needed = shownHeight * Math.min(window.devicePixelRatio || 1, 2);
    return (sources.find((source) => source.height >= needed) || sources[sources.length - 1]).url;
  };

  /** No sound, whatever happens: a video that gets unmuted is muted again. */
  const keepMuted = (video) => {
    video.muted = true;
    video.addEventListener('volumechange', () => {
      if (!video.muted) video.muted = true;
    });
  };

  customElements.define(
    'jc-product-video',
    class extends HTMLElement {
      connectedCallback() {
        this.rebuild = () => this.build();
        document.addEventListener('shopify:section:load', this.rebuild);
        document.addEventListener('shopify:section:unload', this.rebuild);
        this.build();
      }

      /** The row of the section's <template>, or nothing; then the cards come alive. */
      build() {
        this.observer?.disconnect();
        const template = document.querySelector('template[data-jc-product-videos]');
        if (template) this.replaceChildren(template.content.cloneNode(true));
        else this.replaceChildren();

        this.cards = [...this.querySelectorAll('[data-card]')];
        if (!this.cards.length) return;
        this.dialog = this.querySelector('[data-dialog]');
        this.big = this.querySelector('[data-big]');
        this.toggle = this.querySelector('[data-toggle]');
        this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
        this.visible = new Set();
        this.current = null;

        this.observer = new IntersectionObserver((entries) => this.onVisibility(entries), { threshold: VISIBLE });
        this.cards.forEach((card) => {
          const video = card.querySelector('video');
          keepMuted(video);
          video.addEventListener('ended', () => this.playNext());
          card.querySelector('[data-open]').addEventListener('click', () => this.open(card));
          this.observer.observe(card);
        });

        if (this.big) keepMuted(this.big);
        this.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
        this.toggle?.addEventListener('click', () => (this.big.paused ? this.big.play().catch(() => {}) : this.big.pause()));
        this.big?.addEventListener('play', () => this.setToggle(false));
        this.big?.addEventListener('pause', () => this.setToggle(true));
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
        this.dialog?.addEventListener('close', () => this.onClose());

        // On a computer the row has two small arrows when it is longer than the column.
        this.row = this.querySelector('.jc-product-video__row');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.prev?.addEventListener('click', () => this.step(-1));
        this.next?.addEventListener('click', () => this.step(1));
        this.row.addEventListener('scroll', () => this.updateNav(), { passive: true });
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(() => this.updateNav());
        this.resizeObserver.observe(this.row);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        document.removeEventListener('shopify:section:load', this.rebuild);
        document.removeEventListener('shopify:section:unload', this.rebuild);
      }

      mayAutoplay() {
        return !this.reducedMotion.matches && !navigator.connection?.saveData && !this.dialog?.open;
      }

      /** The cards on screen, in row order. */
      onScreen() {
        return this.cards.filter((card) => this.visible.has(card));
      }

      onVisibility(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.visible.add(entry.target);
          } else {
            this.visible.delete(entry.target);
            entry.target.querySelector('video').pause();
          }
        });
        if (!this.current || !this.visible.has(this.current)) this.play(this.onScreen()[0]);
      }

      play(card) {
        this.current = card || null;
        if (!card || !this.mayAutoplay()) return;
        const video = card.querySelector('video');
        if (!video.getAttribute('src')) video.src = pickSource(card, video, card.clientHeight);
        video.play().catch(() => {});
      }

      playNext() {
        const cards = this.onScreen();
        if (!cards.length) return this.play(null);
        const next = cards[(cards.indexOf(this.current) + 1) % cards.length];
        if (next === this.current) next.querySelector('video').currentTime = 0;
        this.play(next);
      }

      // One step is one card plus the gap between two cards.
      step(direction) {
        const items = this.row.children;
        const width = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : this.row.clientWidth;
        this.row.scrollBy({ left: direction * width, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      }

      updateNav() {
        if (!this.prev || !this.next) return;
        const max = this.row.scrollWidth - this.row.clientWidth;
        const scrolls = max > 2;
        this.prev.hidden = !scrolls;
        this.next.hidden = !scrolls;
        this.prev.disabled = this.row.scrollLeft <= 2;
        this.next.disabled = this.row.scrollLeft >= max - 2;
      }

      setToggle(paused) {
        this.dialog.classList.toggle('is-paused', paused);
        this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
      }

      open(card) {
        if (!this.dialog || !this.big) return;
        this.cards.forEach((other) => other.querySelector('video').pause());
        this.trigger = card.querySelector('[data-open]');
        this.dialog.showModal();
        document.documentElement.classList.add('jc-product-video-open');
        this.big.src = pickSource(card, this.big, this.big.clientHeight || window.innerHeight);
        this.big.play().catch(() => this.setToggle(true));
        this.querySelector('[data-close]')?.focus();
      }

      onClose() {
        this.big.pause();
        this.big.removeAttribute('src');
        this.big.load();
        document.documentElement.classList.remove('jc-product-video-open');
        if (this.trigger?.isConnected) this.trigger.focus();
        this.play(this.visible.has(this.current) ? this.current : this.onScreen()[0]);
      }
    },
  );
}
